import { useState } from "react"
import styled from "styled-components"

import { PlusIcon } from "@minuthq/meatball-ui-icons"
import {
  Banner,
  breakpoint,
  Button,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"

import { NoTasks } from "src/components/EmptyState/NoTasks"
import { CreateTaskDialog } from "src/components/TaskManagement/CreateTaskDialog"
import { TaskList } from "src/components/TaskManagement/TaskList"
import { HREF_MINUT_TASKS_FEEDBACK_FORM } from "src/constants/hrefs"
import { usePostCreateTaskInitiated } from "src/data/analytics/queries/taskManagementAnalyticsQueries"
import { useFetchTasksCount } from "src/data/taskManagement/hooks/useTaskCount"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { MButton } from "src/ui/Button/MButton"
import { Container } from "src/ui/Container/Container"
import { MainView } from "src/ui/Layout/MainView"
import { MBadge } from "src/ui/MBadge/MBadge"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { ITab } from "src/ui/Tabs/Tab"
import { TabPanel } from "src/ui/Tabs/TabPanel"
import { Tabs } from "src/ui/Tabs/Tabs"

export function TaskManagementView() {
  const { tPlaceholder } = useTranslate()
  const [openDialog, setOpenDialog] = useState(false)

  const postCreateTaskInitiated = usePostCreateTaskInitiated()

  const {
    count: todoTasksCount,
    loading: todoTasksLoading,
    error: todotasksError,
  } = useFetchTasksCount({ statuses: ["todo"] })
  const {
    count: allTasksCount,
    loading: allTasksLoading,
    error: allTasksError,
  } = useFetchTasksCount({})

  const loading = allTasksLoading || todoTasksLoading

  function handleClickAddTask() {
    setOpenDialog(true)
    postCreateTaskInitiated.mutate()
  }

  return (
    <MainView
      title={
        <TitleWrapper>
          {tPlaceholder("Tasks")}
          <MBadge>{tPlaceholder("Beta access")}</MBadge>
        </TitleWrapper>
      }
      titleBarProps={{
        actionBar: (
          <MButton
            onClick={handleClickAddTask}
            startIcon={<PlusIcon width={20} />}
            hidden={allTasksCount <= 0}
          >
            {tPlaceholder("Create a new task")}
          </MButton>
        ),
        description: tPlaceholder(
          "Manage cleaning, maintenance and operations tasks."
        ),
      }}
    >
      <TasksContent
        allTasksCount={allTasksCount}
        todoTasksCount={todoTasksCount}
        loading={loading}
        isError={allTasksError || todotasksError}
      />

      {openDialog && <CreateTaskDialog onClose={() => setOpenDialog(false)} />}
    </MainView>
  )
}

function TasksContent({
  allTasksCount,
  todoTasksCount,
  loading,
  isError,
}: {
  allTasksCount: number
  todoTasksCount: number
  loading: boolean
  isError?: boolean
}) {
  const { t, langKeys, tPlaceholder } = useTranslate()

  const tabs: ITab[] = [
    {
      label: tPlaceholder(`To-do tasks (${todoTasksCount})`),
      id: Routes.TodoTasks.location().hash!,
      view: <TaskList taskStatus="todo" />,
    },

    {
      label: tPlaceholder("Completed tasks"),
      id: Routes.CompletedTasks.location().hash!,
      view: <TaskList taskStatus="done" />,
    },
  ]

  if (loading) {
    return <TasksSkeleton />
  }

  if (isError) {
    return (
      <Banner type="error">
        {t(langKeys.failed_general_error_try_refreshing_page)}
      </Banner>
    )
  }

  if (allTasksCount <= 0) {
    return <NoTasks />
  }

  return (
    <Tabs labels={tabs}>
      {tabs.map((tab) => (
        <StyledTabPanel key={tab.id}>{tab.view}</StyledTabPanel>
      ))}

      <StyledFeedbackBanner type="info" fullWidth>
        <Container>
          <Text variant="subtitle">
            {tPlaceholder("What do you think about Tasks?")}
          </Text>

          <FeedbackContentGrid>
            <Text>
              {tPlaceholder(
                `You have access to Tasks as part of our Beta program. We will keep improving them over time. Help us make Tasks better by sharing your feedback and unlock Minut credits to keep using tasks past Beta program.`
              )}
            </Text>

            <div>
              <Button
                variant="subtle"
                onClick={() =>
                  window.open(HREF_MINUT_TASKS_FEEDBACK_FORM, "_blank")
                }
              >
                {tPlaceholder("Share feedback")}
              </Button>
            </div>
          </FeedbackContentGrid>
        </Container>
      </StyledFeedbackBanner>
    </Tabs>
  )
}

function TasksSkeleton() {
  return (
    <SkeletonWrapper>
      <MSkeleton variant="rect" height={30} />
      <MSkeleton variant="rect" height={30} />
      <MSkeleton variant="rect" height={30} />
    </SkeletonWrapper>
  )
}

const StyledTabPanel = styled(TabPanel)`
  padding-top: ${spacing.XL2};
`

const SkeletonWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.XS};
`

const StyledFeedbackBanner = styled(Banner)`
  margin-top: ${spacing.XL2};
`

const FeedbackContentGrid = styled.div`
  display: grid;
  gap: ${spacing.XS};

  @container (${breakpoint.mediumUp}) {
    grid-template-columns: 1fr auto;
  }
`
